import React from 'react';
import { FeatherTrophy, FeatherGift } from '@subframe/core';
import { Badge } from '../../ui/components/Badge';
import { Leaderboard } from './Leaderboard';
import { WalletAddress } from './WalletAddress';
import type { ContestView } from '../../api/sweepstakes';

interface ResultsModalProps {
  contestView: ContestView;
  isOpen: boolean;
  onClose: () => void;
}

export const ResultsModal: React.FC<ResultsModalProps> = ({ contestView, isOpen, onClose }) => {
  if (!isOpen) return null;

  const { contest, prizeTiers, stats, winners } = contestView;

  const startDate = new Date(contest.starts_at).toLocaleDateString();
  const endDate = new Date(contest.ends_at).toLocaleDateString();
  const hasWinners = winners && winners.length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-default-background rounded-xl shadow-xl border border-neutral-border max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-default-font">Contest Results</h2>
            <button onClick={onClose} className="text-subtext-color hover:text-default-font text-2xl leading-none">
              &times;
            </button>
          </div>

          {/* Contest Info */}
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-default-font">{contest.name}</h3>
            <p className="text-sm text-subtext-color">{startDate} - {endDate}</p>
            <div className="text-sm text-subtext-color mt-1">
              <span>{stats.entries} total entries from {stats.participants} participants</span>
            </div>
          </div>

          {/* Winners Section */}
          {hasWinners && (
            <div className="mb-6">
              <div className="space-y-3">
                {prizeTiers.map(tier => {
                  const tierWinners = winners?.filter(w => w.prize_tier_id === tier.id) || [];
                  if (tierWinners.length === 0) return null;

                  const isLeaderboard = tier.type === 'leaderboard';

                  return (
                    <div key={tier.id} className="p-4 rounded-lg border border-brand-200 bg-brand-50/30">
                      <div className="flex items-center gap-2 mb-1">
                        {isLeaderboard
                          ? <FeatherTrophy className="h-4 w-4 text-yellow-500" />
                          : <FeatherGift className="h-4 w-4 text-brand-500" />
                        }
                        <span className="font-semibold text-default-font">{tier.name}</span>
                        <Badge variant={isLeaderboard ? 'success' : 'warning'}>
                          {isLeaderboard ? 'Leaderboard' : 'Raffle'}
                        </Badge>
                      </div>
                      <p className="text-sm text-subtext-color mb-3">{tier.description}</p>
                      <div className="space-y-2">
                        {tierWinners.map(w => (
                          <div key={w.id} className="flex items-center gap-2">
                            <span className="text-sm font-bold text-brand-600">
                              {w.rank ? `#${w.rank}` : 'Winner'}
                            </span>
                            <WalletAddress address={w.wallet_address} />
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* No winners yet */}
          {!hasWinners && (
            <div className="mb-6 p-4 rounded-lg border border-neutral-border text-center">
              <p className="text-sm text-yellow-400 font-medium">Winners are being drawn...</p>
              <p className="text-xs text-subtext-color mt-1">Winners are automatically drawn when the contest ends. Check back shortly.</p>
              <div className="mt-2 space-y-2">
                {prizeTiers.map(tier => (
                  <div key={tier.id} className="flex items-center justify-center gap-2 text-sm text-subtext-color">
                    <Badge variant={tier.type === 'leaderboard' ? 'success' : 'warning'}>
                      {tier.type === 'leaderboard' ? 'Leaderboard' : 'Raffle'}
                    </Badge>
                    <span>{tier.name}: {tier.description}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Proof of Payment Links */}
          {contest.proof_links && contest.proof_links.length > 0 && (
            <div className="mb-6 p-4 rounded-lg border border-success-200 bg-success-50/30">
              <h4 className="text-sm font-semibold text-default-font mb-2">Proof of Prize Distribution</h4>
              <p className="text-xs text-subtext-color mb-3">
                The collection creator has provided transaction links verifying that prizes were sent to winners.
              </p>
              <div className="space-y-1.5">
                {contest.proof_links.map((link, i) => (
                  <a
                    key={i}
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-brand-600 hover:underline break-all"
                  >
                    <span className="shrink-0">🔗</span>
                    {link}
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Full Leaderboard */}
          <div>
            <h4 className="font-semibold text-default-font mb-2">Final Leaderboard</h4>
            <Leaderboard contestId={contest.id} frozen />
          </div>
        </div>
      </div>
    </div>
  );
};
